<template>
  <div>
    <d-row no-gutters class="page-header py-4 mb-2">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <template v-if="isInPopup">
          <h4>Lista dokumentów</h4>
        </template>
        <template v-else>
          <h4>Lista dokumentów pacjenta:
            <p class="mt-1 mb-0 patient-name">
              {{ patient.name + ' ' + patient.last_name }}
            </p>
          </h4>
        </template>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="showAddDocPopup"
          :disabled="!loaded">
          <i class="material-icons">add</i>
          Dodaj dokument
        </d-button>
      </d-col>
    </d-row>
    <d-row
      v-if="loaded"
      class="d-flex ml-0 mb-3 page-filters w-100">
      <validation-observer
        ref="documents-filters-form"
        tag="form"
        class="d-flex w-100">
        <d-input
          placeholder="Podaj nazwę dokumentu..."
          v-model="filters.name" />

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>
        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>
    <d-card
      class="w-100"
      style="box-shadow: none;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Typ dokumentu</th>
            <th scope="col" class="border-0">Komentarz</th>
            <th scope="col" class="border-0 text-center">Wzór do pobrania</th>
            <th scope="col" class="border-0 text-center">Skan do pobrania</th>
            <th scope="col" class="border-0 text-center">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(document, documentIndex) of documents"
            :key="'document-' + documentIndex">
            <td class="align-middle">
              {{ document.edocument.name }}
            </td>
            <td class="align-middle">
              {{ document.description }}
            </td>
            <td class="align-middle text-center">
              <d-button
                theme="secondary"
                outline
                :disabled="actionInProgress"
                style="width"
                @click.stop.prevent="downloadDocumentPattern(document.edocument_id, document.edocument.filename)">
                <i class="material-icons">cloud_download</i>
                <span v-if="!isInPopup" class="ml-2">Pobierz</span>
              </d-button>
            </td>
            <td class="align-middle text-center">
              <template v-if="document.filename">
                <d-button
                  theme="info"
                  outline
                  :disabled="actionInProgress"
                  @click.stop.prevent="downloadDocument(document.id, document.filename)">
                  <i class="material-icons">cloud_download</i>
                  <span v-if="!isInPopup" class="ml-2">Pobierz</span>
                </d-button>
              </template>
              <template v-else>
                <span style="opacity: 0.5">Brak</span>
              </template>
            </td>
            <td
              class="align-middle text-center"
              style="width: 130px;">
              <d-button
                theme="primary"
                @click.prevent="showEditDocPopup(document)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.prevent="removeDocument(document.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="documents.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak dokumentów do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <patients-form-docs-popup
      :document="editDoc"
      :is-visible="isShowDocsPopup"
      :on-close-popup="handleCloseDocPopup"
      :on-submit-callback="debouncedLoadFilteredData"
      :patient-id="patient.id"
      :visit-id="visitId"/>
  </div>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import api from '@/api';
import ListUtils from '@/utils/ListUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import debounce from 'lodash.debounce';
import PatientsFormDocsPopup from '@/components/forms/patients/PatientsFormDocsPopup.vue';

export default {
  name: 'patients-form-documents',
  props: {
    filterForVisit: {
      default: false,
      type: Boolean
    },
    isInPopup: {
      default: false,
      type: Boolean
    },
    isVisible: {
      default: false,
      type: Boolean
    },
    patient: {
      required: true,
      type: Object
    },
    visitId: {
      required: false,
      type: Number
    }
  },
  components: {
    'patients-form-docs-popup': PatientsFormDocsPopup
  },
  watch: {
    'isVisible': function (newValue) {
      if (newValue && !this.loaded) {
        this.loadItemsData();
      }
    }
  },
  data () {
    return {
      actionInProgress: false,
      documents: [],
      editDoc: null,
      filters: {
        name: ''
      },
      isShowDocsPopup: false,
      loaded: false,
      loadError: false,
      removeInProgress: false
    }
  },
  mounted () {
    if (this.isVisible) {
      this.loadItemsData();
    }
  },
  methods: {
    loadItemsData () {
      let where = {
        name: this.filters.name
      };

      if (this.filterForVisit) {
        where.visit_id = this.visitId;
      }

      ListUtils.loadItemsData(this, {
        endpoint: '/patients/edocuments/items/' + this.patient.id,
        listField: 'documents',
        pagination: false,
        params: {
          where
        }
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      Vue.set(this, 'loaded', false);
      Vue.set(this, 'loadError', false);

      this.loadItemsData();
    }, 250),
    validateFiltersForm () {
      FormUtils.validate(this.$refs['documents-filters-form'], this.debouncedLoadFilteredData);
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        name: ''
      };
      this.debouncedLoadFilteredData();
    },
    removeDocument (documentID) {
      Vue.set(this, 'removeInProgress', true);

      ListUtils.removeItem(this, {
        endpointBase: '/patients/edocuments/delete/',
        id: documentID,
        successTitle: 'Usunięto dokument',
        successText: 'Wybrany dokument został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten dokument?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie dokumentu nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          Vue.set(this, 'removeInProgress', false);
          this.debouncedLoadFilteredData();
        },
        cancelAction: () => {
          Vue.set(this, 'removeInProgress', false);
        }
      });
    },
    downloadDocument (documentID, name) {
      this.actionInProgress = true;
      let filename = name;

      axios({
        url: `${api}patients/edocuments/get-file/` + documentID,
        method: 'POST',
        responseType: 'blob'
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.addEventListener('click', this.clickCallback, false);
        link.click();
        this.actionInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          type: 'warning',
          confirmButtonText: 'OK',
        });
        this.actionInProgress = false;
      });
    },
    downloadDocumentPattern (patternID, name) {
      this.actionInProgress = true;
      let filename = name;

      axios({
        url: `${api}edocuments/get-file/` + patternID,
        method: 'POST',
        responseType: 'blob'
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.addEventListener('click', this.clickCallback, false);
        link.click();
        this.actionInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          type: 'warning',
          confirmButtonText: 'OK',
        });
        this.actionInProgress = false;
      });
    },
    clickCallback (e) {
      e.stopPropagation();
    },
    showAddDocPopup () {
      Vue.set(this, 'editDoc', null);
      Vue.set(this, 'isShowDocsPopup', true);
    },
    showEditDocPopup (document) {
      Vue.set(this, 'editDoc', document);
      Vue.set(this, 'isShowDocsPopup', true);
    },
    handleCloseDocPopup () {
      Vue.set(this, 'isShowDocsPopup', false);
      Vue.set(this, 'editDoc', null);
    }
  }
}
</script>
